import React, { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import { Logo, IconButton, MessageIcon, ThemeToggle } from "../../design-system";
import { typography, surfaces, borders, textColors, brandColors } from "../../design-system/tokens";
import UserProfileDropdown from "../../core/components/UserProfileDropdown";
import { apiFetch } from "../../core/utils/api";
import { AuthContext } from "../../core/context/AuthContext";

const DashboardNavBar = ({ username, refreshUnreadCount = 0, rightSlot = null }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const { auth } = useContext(AuthContext);

  useEffect(() => {
    if (username) {
      fetchUnreadCount();
    }
  }, [username, refreshUnreadCount]);

  const fetchUnreadCount = async () => {
    try {
      // Fetch all user containers first
      const response = await apiFetch("/api/websites/", {
        credentials: "include",
      });
      
      if (response.ok) {
        const websites = await response.json();
        
        // Extract unique containers
        const containers = websites.reduce((acc, website) => {
          if (website.container_id && !acc.find(c => c.id === website.container_id)) {
            acc.push({
              id: website.container_id,
            });
          }
          return acc;
        }, []);
        
        // Count unread messages from all containers + system inbox
        let totalUnread = 0;
        
        for (const container of containers) {
          const messagesResponse = await apiFetch(
            `/api/containers/${container.id}/contact/?is_read=false`,
            { credentials: "include" }
          );
          
          if (messagesResponse.ok) {
            const unreadMessages = await messagesResponse.json();
            totalUnread += unreadMessages.length;
          }
        }

        // Add unread system inbox messages (MyVitae -> user)
        try {
          const sysResp = await apiFetch(`/api/inbox/messages/?is_read=false`, { credentials: "include" });
          if (sysResp.ok) {
            const data = await sysResp.json();
            const sysUnread = Array.isArray(data?.results) ? data.results.length : Array.isArray(data) ? data.length : 0;
            totalUnread += sysUnread;
          }
        } catch (e) {
          console.error("Failed to fetch system unread count:", e);
        }
        
        setUnreadCount(totalUnread);
      }
    } catch (err) {
      console.error("Failed to fetch unread count:", err);
    }
  };

  const displayCount = unreadCount > 99 ? "99+" : unreadCount.toString();

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 ${surfaces.glass} shadow-sm border-b ${borders.glass} transition-colors duration-300`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative flex justify-between items-center h-12 sm:h-16">
          {/* Left: Logo - always visible, smaller on mobile */}
          <div className="flex items-center">
            <Link to={`/edit/${username}`} className="flex items-center">
              <Logo 
                iconSize="w-6 h-6 sm:w-8 sm:h-8" 
                textSize="text-lg sm:text-2xl" 
                useCurrentColor={false}
                iconColor="#3b82f6"
                textColor={brandColors.gradientText}
              />
            </Link>
          </div>

          {/* Center label hidden on small screens to keep layout clean */}
          <div className="absolute left-1/2 transform -translate-x-1/2 hidden md:block">
            <Link
              to={`/edit/${username}`}
              className={`${typography.body} ${textColors.primary} ${brandColors.primaryHover} transition-colors duration-200`}
            >
              Dashboard
            </Link>
          </div>

          {/* Right: username, messages, avatar */}
          <div className="flex items-center space-x-2 sm:space-x-4">
            {rightSlot ? <div className="flex items-center">{rightSlot}</div> : null}
            <div className={`hidden md:block ${textColors.secondary} truncate max-w-[40vw] sm:max-w-none`}>
              <span className={`hidden sm:inline ${typography.bodySmall}`}>Welcome back,</span>
              <span className={`sm:ml-1 ${typography.h5} ${brandColors.primary} truncate inline-block align-middle`}>
                {username}
              </span>
            </div>
            {/* Messages Icon */}
            <div className="relative">
              <Link to={`/dashboard/${username}/messages`}>
                <IconButton
                  variant="ghost"
                  icon={<MessageIcon size="lg" />}
                  title="Messages"
                  aria-label="Messages"
                  className={`${brandColors.primary} ${brandColors.primaryHover}`}
                />
              </Link>
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-green-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center min-w-5">
                  {displayCount}
                </span>
              )}
            </div>
            {/* Theme Toggle - Blue moon icon with blue fill on hover */}
            <ThemeToggle moonColor="text-blue-600 dark:text-blue-400" moonHoverFill="hover:fill-blue-600 dark:hover:fill-blue-400" />
            {/* User Avatar Dropdown */}
            <UserProfileDropdown 
              username={username} 
              showDashboardLink={true} 
              showSettings={true} 
            />
          </div>
        </div>
      </div>
    </nav>
  );
};

export default DashboardNavBar; 